import { useCallback, useEffect, useRef } from 'react';

import { useOrientation } from '../../boot/OrientationContext';
import {
  GET_LAYOUT_MAX_WIDTH,
  GET_LIST_ITEM_NUM,
} from '../../constants/layout';
import { checkItemViewed } from '../../context/devLogFirehose';
import NoMoreItemsView from '../../elements/NoMoreItemsView';
import RefreshControl from '../../elements/RefreshControl';
import useActions from '../../hooks/useActions';
import { translate } from '../../i18n';
import { rootAction } from '../../redux';
import { NewArrivalItem } from '../../types/NewArrivalResponse';
import ItemSources from '../../utils/ItemSources';
import * as navigationService from '../../utils/navigationService';
import routeUtils from '../../utils/routes';
import FlatListWithImpression from '../FlatListWithImpression';
import { itemProducer } from '../FlatListWithImpression/utils';
import ListItem from '../ListItem';
import ProductListMeta from '../ProductListMeta';
import { useData } from './hooks';

const { getNewArrivalActions } = rootAction.actionsList;

const keyExtractor = item => `${item}`;
const ITEM_MARGIN = 5;
const LAYOUT_NAME = 'new_arrivals';

interface Props {
  isVisible: boolean;
  route: {
    index: number;
    key: string;
    title: string;
    params: {
      root_category_ids?: number[];
    };
  };
  bindScrollToTop: (func: Function, key: string) => void;
}

const NewArrivalsPage = (props: Props) => {
  const { route, bindScrollToTop, isVisible } = props;

  useOrientation();
  const getNewArrivals = useActions(getNewArrivalActions.request);
  const { currentPage, isFetching, hasMoreItems, products, dispatch } =
    useData();

  const impressionList = useRef<typeof FlatListWithImpression>();
  const sources = useRef(
    new ItemSources<NewArrivalItem>({
      productKey: product => `${product.id}`,
      productSource: product => product.source,
    })
  );

  const fetchData = useCallback(
    ({ type, page }) => {
      getNewArrivals({
        loadingType: type,
        page,
        success: sources.current.processSource,
        dispatch,
        ...route.params,
      });
    },
    [getNewArrivals, route]
  );

  const hasNewFetch = useRef(false);

  useEffect(() => {
    if (isVisible && !hasNewFetch.current) {
      fetchData({
        type: 'new',
        page: 1,
      });

      hasNewFetch.current = true;
    }
  }, [isVisible]);

  const handleRefresh = useCallback(() => {
    fetchData({
      type: 'reload',
      page: 1,
    });

    const list = impressionList.current;

    if (typeof list?.clearImpressionAccumulatedItems === 'function') {
      list.clearImpressionAccumulatedItems();
    }
  }, [fetchData]);

  const handleEndReached = useCallback(() => {
    if (!isFetching && hasMoreItems) {
      fetchData({
        type: 'loadMore',
        page: currentPage + 1,
      });
    }
  }, [fetchData, isFetching, hasMoreItems, currentPage]);

  const renderItem = useCallback(({ item, index }) => {
    return (
      <ListItem
        index={index}
        key={item}
        id={item}
        showLoading={`${item}`.includes('blank')}
        itemWidth={GET_LAYOUT_MAX_WIDTH() / GET_LIST_ITEM_NUM()}
        itemMargin={ITEM_MARGIN}
        layoutName={LAYOUT_NAME}
        showLovesElement
        source={sources.current.get(item)}
        shouldDisplayImpressionForDebug={checkItemViewed(
          impressionList.current?.accumulatedItems,
          item
        )}
        linesOfTitle={1}
        showSKUTag
        showVendor
      />
    );
  }, []);

  const renderFooter = useCallback(() => {
    if (hasMoreItems) {
      return null;
    }

    const actionBtnWebProps = {
      accessibilityRole: 'link',
      href: routeUtils.compileToPath('Home'),
    };

    return (
      <NoMoreItemsView
        actionBtnText={translate('backToHomePage')}
        actionBtnWebProps={actionBtnWebProps}
        onActionBtnPress={() => {
          navigationService.reset();
        }}
        useActionBtn
      />
    );
  }, [hasMoreItems]);

  const numColumns = GET_LIST_ITEM_NUM();

  useEffect(() => {
    bindScrollToTop(() => {
      impressionList.current?.listCurrent?.scrollToOffset({
        animated: true,
        offset: 0,
      });
    }, route.key);
  }, [bindScrollToTop, route.key]);

  return (
    <>
      <ProductListMeta
        title={translate('new-arrivals')}
        description={translate('new-arrivals-desc')}
        firstId={products[0]}
      />
      <FlatListWithImpression
        ref={impressionList}
        numColumns={numColumns}
        key={numColumns}
        data={products}
        renderItem={renderItem}
        onEndReached={handleEndReached}
        layoutName={LAYOUT_NAME}
        keyExtractor={keyExtractor}
        ListFooterComponent={renderFooter}
        dataProducer={itemProducer}
        sources={sources.current}
        isVisible={isVisible}
        refreshControl={
          <RefreshControl onRefresh={handleRefresh} isFetching={isFetching} />
        }
      />
    </>
  );
};

export default NewArrivalsPage;
